import { useEffect, useMemo, useState, useCallback } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "sonner";
import { Plus, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MemberAvatar } from "@/components/MemberAvatar";
import { api } from "@/lib/api";
import { useMembers } from "@/context/MemberContext";
import { doneToday, isDue } from "@/lib/recurrence";
import { useMidnightRefresh } from "@/lib/useMidnight";
import { PageHeader, EmptyState, MemberFilterBar } from "@/components/shared";
import { ChoreCard } from "@/components/chores/ChoreCard";
import { ChoreDialog } from "@/components/chores/ChoreDialog";

const EMPTY = {
  title: "",
  points: 5,
  member_ids: [],
  frequency: "daily",
  interval: 1,
  days_of_week: [],
};

const assignedTo = (c, mid) => c.member_id === mid || (c.member_ids || []).includes(mid);

const recurOf = (c) => ({ frequency: c.frequency || "daily", interval: c.interval || 1, days_of_week: c.days_of_week || [] });
const isChoreDue = (c) => isDue(recurOf(c), c.last_completed_at || null);

export default function Chores() {
  const { members } = useMembers();
  const [chores, setChores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api("/chores");
      setChores(data.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useMidnightRefresh(load);

  const visible = useMemo(
    () => (filter ? chores.filter((c) => assignedTo(c, filter)) : chores),
    [chores, filter]
  );
  // Only chores due today are actionable; completed-today ones show in a "Done" area.
  const pending = useMemo(() => visible.filter((c) => isChoreDue(c)), [visible]);
  const done = useMemo(
    () => visible.filter((c) => !isChoreDue(c) && doneToday(c.last_completed_at)),
    [visible]
  );

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      title: c.title,
      points: c.points,
      member_ids: c.member_ids?.length ? c.member_ids : c.member_id ? [c.member_id] : [],
      frequency: c.frequency,
      interval: c.interval || 1,
      days_of_week: c.days_of_week || [],
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Give the chore a name");
      return;
    }
    setSaving(true);
    const body = {
      title: form.title.trim(),
      points: Number(form.points) || 0,
      member_ids: form.member_ids,
      member_id: null,
      frequency: form.frequency,
      interval: form.interval,
      days_of_week: form.frequency === "weekly" ? form.days_of_week : [],
    };
    try {
      if (editing) await api(`/chores/${editing.id}`, { method: "PUT", body });
      else await api("/chores", { method: "POST", body });
      setDialogOpen(false);
      await load();
      toast.success(editing ? "Chore updated" : "Chore added");
    } catch {
      toast.error("Could not save chore");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!editing) return;
    try {
      await api(`/chores/${editing.id}`, { method: "DELETE" });
      setDialogOpen(false);
      await load();
      toast.success("Chore deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const complete = async (c) => {
    if (doneToday(c.last_completed_at)) return;
    setChores((prev) =>
      prev.map((x) =>
        x.id === c.id ? { ...x, last_completed_at: new Date().toISOString(), streak: (x.streak || 0) + 1 } : x
      )
    );
    try {
      const res = await api("/chores/complete", { method: "POST", body: { chore_id: c.id } });
      toast.success(`Nice! +${res.points_awarded} pts`);
    } catch {
      toast.error("Could not complete");
      load();
    }
  };

  const onDragEnd = async (result) => {
    if (!result.destination || filter) return;
    const items = Array.from(pending);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    const orderedIds = items.map((c) => c.id);
    const rest = chores.filter((c) => !orderedIds.includes(c.id)).map((c) => c.id);
    const full = [...orderedIds, ...rest];
    setChores((prev) => [...prev].sort((a, b) => full.indexOf(a.id) - full.indexOf(b.id)));
    try {
      await api("/chores/reorder", { method: "POST", body: { ordered_ids: full } });
    } catch {
      load();
    }
  };

  return (
    <div>
      <PageHeader
        title="Chores"
        subtitle="Assign tasks, build streaks, earn points"
        action={
          <Button className="rounded-full" onClick={openNew} data-testid="add-chore-btn">
            <Plus className="mr-1.5 h-4 w-4" /> New chore
          </Button>
        }
      />

      {members.length > 0 && <MemberFilterBar members={members} value={filter} onChange={setFilter} />}

      {loading ? (
        <div className="mt-4 space-y-3">
          {["s1", "s2", "s3"].map((k) => (
            <Skeleton key={k} className="h-20 w-full rounded-3xl" />
          ))}
        </div>
      ) : pending.length === 0 && done.length === 0 ? (
        <EmptyState
          emoji="🧹"
          title="All clear!"
          message={filter ? "Nothing due for this member today." : "Nothing due today. Add a chore or enjoy the break!"}
          action={
            <Button className="rounded-full" onClick={openNew}>
              <Plus className="mr-1.5 h-4 w-4" /> Add a chore
            </Button>
          }
        />
      ) : (
        <>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="chores">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps} className="mt-4 space-y-3">
                  {pending.map((c, index) => (
                    <Draggable key={c.id} draggableId={c.id} index={index} isDragDisabled={!!filter}>
                      {(dp, snapshot) => (
                        <ChoreCard
                          chore={c}
                          assignees={members.filter((m) => assignedTo(c, m.id))}
                          done={false}
                          filter={filter}
                          dp={dp}
                          snapshot={snapshot}
                          onComplete={complete}
                          onEdit={openEdit}
                        />
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          {done.length > 0 && (
            <div className="mt-6">
              <h2 className="mb-2 px-1 font-display text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Completed today
              </h2>
              <div className="space-y-3 opacity-70">
                {done.map((c) => (
                  <div key={c.id} className="bento-tile flex items-center gap-3 p-4" data-testid={`chore-done-${c.id}`}>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-mint bg-mint text-white">
                      <Check className="h-5 w-5" strokeWidth={3} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-display font-semibold text-ink line-through">{c.title}</p>
                      <p className="text-xs text-muted-foreground">+{c.points} pts earned today</p>
                    </div>
                    <div className="flex -space-x-2">
                      {members.filter((m) => assignedTo(c, m.id)).slice(0, 3).map((m) => (
                        <MemberAvatar key={m.id} member={m} size="sm" />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <ChoreDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        form={form}
        setForm={setForm}
        members={members}
        saving={saving}
        onSave={save}
        onDelete={remove}
      />
    </div>
  );
}
